import { Request } from '@transsion/request'
import { parseQuery, parseUrl, stringifyQuery } from '@transsion/utils/url'

import {
  AUTH_DEPT_CHILD_PATH,
  AUTH_LOGIN_PATH,
  AUTH_LOGOUT_PATH,
  AUTH_RTOKEN_CHECK_PATH,
  AUTH_RTOKEN_GET_PATH,
  AUTH_USER_INFO_PATH,
  AUTH_USER_PAGE_PATH,
  HEADER_APPID,
  HEADER_AUTH,
  HEADER_REQUEST_ID,
  HEADER_RTOKEN,
  LOGIN_CALLBACK_QUERIES,
} from './constants'
import { getDefaultBaseUrl, getDefaultStorePrefixKey, getFallbackLanguage } from './defaults'
import { TokenType } from './enums'
import {
  InvalidLoginPayload,
  InvalidPaylodUrlError,
  InvalidRedirectUrlError,
  InvalidRequestTokenError,
  InvalidUserTokenError,
} from './errors'
import type { Authentication, AuthenticationStorage, LanguageHolder } from './interfaces'
import { normalizeEnv, normalizeLanguageHolder, normalizeRequestId } from './normalize'
import InternalStorage from './storage'
import type {
  AuthConfig,
  BaseUrlCallback,
  Dept,
  Env,
  LangCallback,
  PageResponse,
  PayloadData,
  QueryChildDeptListOptions,
  QueryUsersOptions,
  RequestIdCallback,
  ServerLocation,
  ServiceType,
  TokenData,
  User,
  UserInfo,
} from './types'

export interface Options {
  /** 应用配置 */
  appConfig: AuthConfig
  /** 基础地址 */
  baseUrl?: string | BaseUrlCallback
  /** 环境 */
  env?: Env
  /** 服务器地址 */
  location?: ServerLocation
  /** 语言 */
  lang?: string | LangCallback | LanguageHolder
  /** 请求ID */
  requestId?: string | RequestIdCallback
  /** 本地存储前缀 */
  storagePrefixKey?: string
  /** 本地存储 */
  storage?: AuthenticationStorage
}

export default class Auth implements Authentication {
  readonly serviceType: ServiceType = 'auth'

  #appConfig: AuthConfig

  #baseUrl: string

  #languageHolder: LanguageHolder

  #storage: AuthenticationStorage

  #request: Request

  constructor(opts: Options) {
    if (!opts.appConfig?.authId || !opts.appConfig.authCode) {
      throw new TypeError(
        `The ${this.serviceType} service requires appConfig.authId and appConfig.authCode`,
      )
    }

    const env = normalizeEnv(opts.env)

    this.#baseUrl =
      typeof opts.baseUrl === 'function'
        ? opts.baseUrl(this.serviceType, env, opts.location)
        : (opts.baseUrl ?? getDefaultBaseUrl(this.serviceType, env, opts.location))

    this.#appConfig = opts.appConfig

    if (opts.storage) {
      this.#storage = opts.storage
    } else {
      const storagePrefixKey =
        opts.storagePrefixKey ?? getDefaultStorePrefixKey(opts.appConfig.authCode)

      this.#storage = new InternalStorage({
        prefixKey: storagePrefixKey,
      })
    }

    this.#languageHolder = normalizeLanguageHolder(opts.lang)

    const req = new Request({
      baseURL: this.#baseUrl,
      headers: {
        [HEADER_REQUEST_ID]: normalizeRequestId(opts.requestId),
      },
    })

    req.addResponseInterceptors(({ data }) => {
      if (data?.code !== '200' || !data.data) {
        throw new Error(data?.message || 'Invalid response')
      }

      return data.data
    })

    this.#request = req
  }

  /**
   * Get login url with redirect url.
   *
   * @param redirectUrl {String} redirect url
   * @param extraQueries {Record<string, unknown>} extra queries
   * @returns {String} login url
   */
  public getLoginUrl(redirectUrl: string, extraQueries?: Record<string, unknown>): string {
    if (!redirectUrl) {
      throw InvalidRedirectUrlError
    }

    const cleanedUrl = this.cleanLoginPayload(redirectUrl)
    const encodedRedirectUrl: string = encodeURIComponent(cleanedUrl)

    const lang: string = this.#languageHolder.getLang() ?? getFallbackLanguage()
    const source = this.#appConfig.authCode
    const appId = this.#appConfig.authId

    let url = `${this.#baseUrl}${AUTH_LOGIN_PATH}?source=${source}&redirect=${encodedRedirectUrl}&lang=${lang}&appId=${appId}&type=simple`

    if (extraQueries) {
      url += `&${stringifyQuery(extraQueries)}`
    }

    return url
  }

  /**
   * Parse the login payload url
   *
   * @param url {String} login url with payload
   * @returns {PayloadData | null} PayloadData
   */
  public parseLoginPayload(url: string): PayloadData | null {
    if (!url?.includes('?')) {
      return null
    }

    const parsedUrl = parseUrl(url)

    const extractQuery = (q: unknown) => {
      if (!q) return ''

      return Array.isArray(q) && q.length > 0 ? q[0] : q
    }

    // token and rtoken are in the hash when use the Vue hash mode
    for (const searchKey of ['hash', 'search'] as const) {
      const str = decodeURIComponent(parsedUrl[searchKey] || '')

      if (!!str && str.includes('?')) {
        const payload = parseQuery(str.slice(str.indexOf('?')), {
          ignoreQueryPrefix: true,
        })

        for (const [key, value] of Object.entries(payload)) {
          payload[key] = String(extractQuery(value))
        }

        return Object.keys(payload).reduce<PayloadData>((curr, prev) => {
          const key = prev as keyof PayloadData

          const val = extractQuery(payload[key])

          curr[key] = key === 'expiresIn' ? Number.parseInt(val) : String(val)

          return curr
        }, {})
      }
    }

    return null
  }

  /**
   * Clean the login payload url
   *
   * @param url the url to clean
   * @param extraQueries extra queries to remove
   *
   * @returns
   */
  public cleanLoginPayload(url: string, extraQueries: string[] = []): string {
    if (!url) {
      throw InvalidPaylodUrlError
    } else if (!url.includes('?')) {
      return url
    }

    const parsedUrl = parseUrl(url)

    for (const searchKey of ['hash', 'search'] as const) {
      const str: string = decodeURIComponent(parsedUrl[searchKey])

      if (!!str && str.includes('?')) {
        const query = parseQuery(str.slice(str.indexOf('?')), {
          ignoreQueryPrefix: true,
        })

        // remove query params, this will be added by the sso service
        // below token is request token, rtoken is user token
        for (const key of [...LOGIN_CALLBACK_QUERIES, ...extraQueries]) {
          if (key in query) {
            delete query[key]
          }
        }

        const newQuery: string = stringifyQuery(query, { addQueryPrefix: true })

        parsedUrl[searchKey] = `${str.slice(0, str.indexOf('?'))}${newQuery}`
      }
    }

    return parsedUrl.toString()
  }

  public storeLoginPayload(payload: Partial<PayloadData>): void {
    const { rtoken, utoken } = this.#getPayloadToken(payload)

    if (!rtoken || !utoken) {
      throw InvalidLoginPayload
    }

    this.#storage.setRtoken(rtoken)
    this.#storage.setUtoken(utoken)

    if (payload.expiresIn) {
      this.#storage.setExpiresIn?.(payload.expiresIn)
    }

    if (payload.employeeNo) {
      this.#storage.setEmployeeNo?.(payload.employeeNo)
    }

    if (payload.lang) {
      this.#languageHolder.setLang(payload.lang)
    }
  }

  /**
   * Handle SSO login callback URL
   *
   * @param payload {Record<TokenType, string>|String} url string or tokens
   * @returns {Promise<UserInfo>}
   */
  public async handleLogin(payload: Partial<PayloadData> | string): Promise<UserInfo> {
    if (!payload) {
      throw InvalidLoginPayload
    }

    // clear storage inmediately in case of login
    await this.#storage.clear()

    const parsedPayload: Partial<PayloadData> | null =
      typeof payload === 'string' ? this.parseLoginPayload(payload) : payload

    const { rtoken, utoken } = this.#getPayloadToken(parsedPayload)

    if (!parsedPayload || !rtoken || !utoken) {
      throw InvalidLoginPayload
    }

    let employeeNo = parsedPayload.employeeNo

    const user = await this.getUserInfo(rtoken, utoken)

    // get the actual employeeNo from user info
    if (user.employeeNo) {
      employeeNo = user.employeeNo
    }

    this.storeLoginPayload({
      rtoken,
      utoken,
      employeeNo,
      lang: parsedPayload.lang,
      expiresIn: parsedPayload.expiresIn,
    })

    return user
  }

  /**
   * check the tokens
   *
   * @param rtoken {String} request token
   * @param utoken {String} user token
   * @returns
   */
  public async validateToken(rtoken?: string, utoken?: string): Promise<boolean> {
    const computedRtoken = rtoken || this.#storage.getRtoken()
    const computedUtoken = utoken || this.#storage.getUtoken()

    if (!computedRtoken) {
      throw InvalidRequestTokenError
    }

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    return this.#request.post<boolean>(
      AUTH_RTOKEN_CHECK_PATH,
      {
        rtoken: computedRtoken,
        utoken: computedUtoken,
        appId,
      },
      {
        headers: {
          [HEADER_AUTH]: computedRtoken,
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )
  }

  /**
   * Get user info with tokens
   *
   * @param rtoken {String} request token
   * @param utoken {String} user token
   * @returns {Promise<UserInfo>} user info
   */
  public async getUserInfo(rtoken?: string, utoken?: string): Promise<UserInfo> {
    const computedRtoken = rtoken || this.#storage.getRtoken()
    const computedUtoken = utoken || this.#storage.getUtoken()

    if (!computedRtoken) {
      throw InvalidRequestTokenError
    }

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    const res = await this.#request.post<UserInfo>(
      AUTH_USER_INFO_PATH,
      {
        rtoken: computedRtoken,
        utoken: computedUtoken,
        appId,
      },
      {
        headers: {
          [HEADER_AUTH]: computedRtoken,
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )

    if (!rtoken) {
      this.#storage.setUserInfo(res)
    }

    return res
  }

  /**
   * Refresh token with user token
   *
   * @param utoken {String} user token
   * @returns {Promise<TokensWithExpires>} new tokens
   */
  public async refreshToken(utoken?: string): Promise<TokenData> {
    const computedUtoken = utoken || this.#storage.getUtoken()

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    const res = await this.#request.post<TokenData>(
      AUTH_RTOKEN_GET_PATH,
      {
        utoken: computedUtoken,
        appId,
      },
      {
        headers: {
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )

    if (!utoken) {
      this.storeLoginPayload({
        rtoken: res.rtoken,
        utoken: res.utoken,
        expiresIn: res.expiresIn,
      })
    }

    return res
  }

  /**
   * 获取用户分页列表
   *
   * @param opts {QueryUsersOptions} options
   * @returns {Promise<PageResponse<User>>} user list
   */
  public async queryUsers(opts?: QueryUsersOptions): Promise<PageResponse<User>> {
    const computedRtoken = opts?.rtoken || this.#storage.getRtoken()
    const computedUtoken = opts?.utoken || this.#storage.getUtoken()

    if (!computedRtoken) {
      throw InvalidRequestTokenError
    }

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    return this.#request.post<PageResponse<User>>(
      AUTH_USER_PAGE_PATH,
      {
        current: opts?.current ?? 1,
        size: opts?.size ?? 10,
        searchTotal: opts?.searchTotal ?? false,
        param: {
          appId,
          companyId: opts?.companyId,
          keyword: opts?.keyword,
          deptId: opts?.deptId,
        },
      },
      {
        headers: {
          [HEADER_AUTH]: computedRtoken,
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )
  }

  /**
   * 获取子部门列表
   *
   * @param opts {QueryChildDeptListOptions} options
   * @returns {Promise<Dept>} dept list
   */
  public async queryChildDeptList(opts: QueryChildDeptListOptions): Promise<Dept[]> {
    const computedRtoken = opts.rtoken || this.#storage.getRtoken()
    const computedUtoken = opts.utoken || this.#storage.getUtoken()

    if (!computedRtoken) {
      throw InvalidRequestTokenError
    }

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    return this.#request.post<Dept[]>(
      AUTH_DEPT_CHILD_PATH,
      {
        companyId: opts?.companyId,
        deptId: opts?.deptId,
        appId,
      },
      {
        headers: {
          [HEADER_AUTH]: computedRtoken,
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )
  }

  /**
   * Logout with user token
   *
   * @param utoken User token
   * @returns {Promise<boolean>} logout result
   */
  public async logout(utoken?: string): Promise<boolean> {
    const computedUtoken = utoken || this.#storage.getUtoken()

    if (!computedUtoken) {
      throw InvalidUserTokenError
    }

    const appId = this.#appConfig.authId

    const done = await this.#request.post<boolean>(
      AUTH_LOGOUT_PATH,
      {
        utoken: computedUtoken,
        appId,
      },
      {
        headers: {
          [HEADER_RTOKEN]: computedUtoken,
          [HEADER_APPID]: appId,
        },
      },
    )

    if (!utoken && done) {
      await this.#storage.clear()
    }

    return done
  }

  #getPayloadToken(payload?: Partial<PayloadData> | null): Partial<Record<TokenType, string>> {
    if (!payload) return {}

    return {
      [TokenType.REQUEST_TOKEN]: payload.utoken ? payload.rtoken : payload.token,
      [TokenType.USER_TOKEN]: payload.utoken ?? payload.rtoken,
    }
  }

  /**
   * rtoken 可能已经过期，基于用户平台回调的 expiresIn 判断
   * token 过期时间在 10 秒之内都算过期
   */
  public isInternalRtokenValid(): boolean {
    const rtoken = this.#storage.getRtoken()

    if (!rtoken) return false

    const expireTime = this.#storage.getExpireTime?.()

    // 如果没有过期时间，则认为没有过期
    return expireTime ? expireTime > Date.now() + 10 * 1000 : true
  }
}
